import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import usmApi from "@/lib/usmApi";
import { useUsmAuth } from "@/contexts/UsmAuthContext";
import { PageHeader } from "../UsmLayout";
import Can from "@/components/usm/Can";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Ban, Truck } from "lucide-react";

const STATUS = {
  draft:      { l: "Taslak",       cls: "text-stone-700 bg-stone-50 border-stone-200" },
  in_transit: { l: "Yolda",        cls: "text-amber-700 bg-amber-50 border-amber-200" },
  completed:  { l: "Tamamlandı",   cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  cancelled:  { l: "İptal",        cls: "text-red-600 bg-red-50 border-red-200" },
};

export default function TransferDetail() {
  const { tid } = useParams();
  const { employee, isSuperAdmin, hasPerm } = useUsmAuth();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await usmApi.get(`/transfers/${tid}`);
      setT(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Transfer yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tid]);

  const receive = async () => {
    if (!window.confirm("Tüm ürünler bu şubede teslim alındı olarak işaretlensin mi?")) return;
    setActing(true);
    try { await usmApi.post(`/transfers/${tid}/receive`); toast.success("Transfer tamamlandı"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Teslim alma başarısız"); }
    finally { setActing(false); }
  };

  const cancel = async () => {
    const reason = window.prompt("İptal nedeni:");
    if (reason === null) return;
    setActing(true);
    try { await usmApi.post(`/transfers/${tid}/cancel`, { reason }); toast.success("Transfer iptal edildi"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "İptal başarısız"); }
    finally { setActing(false); }
  };

  if (loading) return <div className="p-6 text-[#71717A]">Yükleniyor...</div>;
  if (!t) return <div className="p-6 text-red-600">Transfer bulunamadı</div>;

  const status = STATUS[t.status] || { l: t.status, cls: "" };
  const canReceive = t.status === "in_transit" && hasPerm("transfers.manage") && (isSuperAdmin || employee?.branch_id === t.destination_branch_id);
  const canCancel  = t.status === "in_transit" && hasPerm("transfers.manage") && (isSuperAdmin || employee?.branch_id === t.source_branch_id);

  return (
    <div data-testid="usm-transfer-detail">
      <PageHeader title={`Transfer ${t.transfer_no}`}
        breadcrumbs={[{ label: "Panel", to: "/usm" }, { label: "Transferler", to: "/usm/transfers" }, { label: t.transfer_no }]}
        actions={
          <div className="flex items-center gap-2">
            <Link to="/usm/transfers" className="text-xs uppercase tracking-[0.2em] px-4 py-2.5 border border-[#E7E5E4] hover:bg-[#FAFAF9] inline-flex items-center gap-2">
              <ArrowLeft size={14} /> Geri
            </Link>
            {canCancel && (
              <Can perm="transfers.manage">
                <button data-testid="transfer-cancel-btn" onClick={cancel} disabled={acting}
                  className="text-xs uppercase tracking-[0.2em] px-4 py-2.5 border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-2">
                  <Ban size={14} /> İptal Et
                </button>
              </Can>
            )}
            {canReceive && (
              <Can perm="transfers.manage">
                <button data-testid="transfer-receive-btn" onClick={receive} disabled={acting}
                  className="bg-[#0A0A0A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] px-5 py-2.5 inline-flex items-center gap-2">
                  <CheckCircle2 size={14} /> Teslim Al
                </button>
              </Can>
            )}
          </div>
        } />

      <div className="bg-white border border-[#E7E5E4] p-6 mb-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <Info label="Transfer No" value={t.transfer_no} mono />
          <Info label="Durum"><span className={"inline-block text-[11px] uppercase tracking-[0.15em] px-2 py-1 border " + status.cls}>{status.l}</span></Info>
          <Info label="Tarih" value={new Date(t.created_at).toLocaleString("tr-TR")} />
          <Info label="Oluşturan" value={t.creator?.full_name || t.creator?.username || "—"} />
          <Info label="Kaynak" value={t.source_branch?.name || "—"} />
          <Info label="Hedef"><span className="inline-flex items-center gap-1.5"><Truck size={12} className="text-[#71717A]" />{t.destination_branch?.name || "—"}</span></Info>
          <Info label="Toplam Ürün" value={String(t.items_count ?? (t.items || []).length)} mono />
          <Info label="Toplam Adet" value={String(t.total_quantity ?? (t.items || []).reduce((a, i) => a + (parseInt(i.quantity, 10) || 0), 0))} mono />
          {t.received_at && <Info label="Teslim Alma" value={new Date(t.received_at).toLocaleString("tr-TR")} />}
          {t.cancelled_at && <Info label="İptal Tarihi" value={new Date(t.cancelled_at).toLocaleString("tr-TR")} />}
        </div>
        {t.note && <div className="mt-4 text-sm text-[#52525B]"><span className="text-[#71717A]">Not:</span> {t.note}</div>}
        {t.cancel_reason && <div className="mt-2 text-sm text-red-600"><span className="text-[#71717A]">İptal nedeni:</span> {t.cancel_reason}</div>}
      </div>

      <div className="bg-white border border-[#E7E5E4] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAF9] text-[11px] uppercase tracking-[0.15em] text-[#71717A]">
            <tr>
              <th className="text-left px-3 py-2.5">Görsel</th>
              <th className="text-left px-3 py-2.5">Stok Kodu</th>
              <th className="text-left px-3 py-2.5">Ürün</th>
              <th className="text-left px-3 py-2.5">Materyal / Ağırlık</th>
              <th className="text-right px-3 py-2.5">Transfer Adedi</th>
            </tr>
          </thead>
          <tbody>
            {(t.items || []).map((it) => (
              <tr key={it.id} data-testid={`transfer-item-${it.stock_code}`} className="border-t border-[#F5F5F4]">
                <td className="px-3 py-2.5">
                  {it.photo_url ? <img src={it.photo_url} alt="" className="h-10 w-10 object-cover" /> : <div className="h-10 w-10 bg-[#FAFAF9] border border-[#F5F5F4]" />}
                </td>
                <td className="px-3 py-2.5 font-mono text-[12px]">{it.stock_code}</td>
                <td className="px-3 py-2.5 font-medium">{it.product_name}</td>
                <td className="px-3 py-2.5 text-[#52525B]">{it.material || "—"}{it.weight_grams != null && ` · ${it.weight_grams}g`}</td>
                <td className="px-3 py-2.5 text-right font-finance font-semibold" data-testid={`transfer-item-qty-${it.stock_code}`}>{it.quantity ?? 1}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Info({ label, value, mono, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{label}</div>
      <div className={mono ? "font-mono" : ""}>{children || value}</div>
    </div>
  );
}
